import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Pill,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import { AcademicYearService } from "../../../services/AcademicYearService";
import { TermsContext } from "../../../utils/Context";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import AcademicYearModal from "./AcademicYearModal";

const AcademicYearCard = ({ academicYear }) => {
  const { setAcademicYears } = useContext(TermsContext);
  const showNotification = useNotification();
  const [academicYearModal, setAcademicYearModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const handleUpdateAcademicYear = async (
    name,
    startDate,
    endDate,
    insightsTerms
  ) => {
    await AcademicYearService.updateAcademicYear(academicYear.id, {
      name: name,
      start_at: startDate,
      end_at: endDate,
      insightsTerms: insightsTerms,
    })
      .then((updatedYear) => {
        setAcademicYears((prevYears) =>
          prevYears.map((year) =>
            year.id === updatedYear.id ? updatedYear : year
          )
        );
        setAcademicYearModal(false);
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Academic Year" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.updateError", { obj: "Academic Year" }),
          ERROR
        )
      );
  };

  const handleDeleteAcademicYear = async () => {
    await AcademicYearService.deleteAcademicYear(academicYear.id)
      .then(() => {
        setAcademicYears((prevYears) =>
          prevYears.filter((year) => year.id !== academicYear.id)
        );
        setDeleteModal(false);
        showNotification(
          t("notifications.general.deleteSuccess", { obj: "Academic Year" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.updateError", { obj: "Academic Year" }),
          ERROR
        )
      );
  };

  return (
    <Card
      style={{
        color: "#0D323F",
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        borderRadius: 10,
        padding: "10px 15px",
      }}
    >
      <Flex gap="small" alignItems="start" wrap="wrap">
        <Flex.Item shouldGrow shouldShrink>
          <Text weight="bold">{academicYear.name}</Text>
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("academicYears.editYear")}
            renderIcon={IconEditLine}
            onClick={() => setAcademicYearModal(true)}
            size="small"
          />
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("academicYears.deleteYear")}
            renderIcon={IconTrashLine}
            onClick={() => setDeleteModal(true)}
            size="small"
          />
        </Flex.Item>
      </Flex>
      <Flex wrap="wrap">
        <Flex.Item size="100%">
          {t("termManagement.dateRange")}:{" "}
          <Pill>{`${new Date(
            academicYear.start_at
          ).toLocaleDateString()} - ${new Date(
            academicYear.end_at
          ).toLocaleDateString()}`}</Pill>
        </Flex.Item>
        <Flex.Item size="100%">
          <Text lineHeight="double">
            {t("academicYears.associatedTerms")}:{" "}
            {academicYear.insights_terms
              .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
              .map((t) => (
                <Pill key={t.id} color="info" margin="0 small 0 0">
                  {t.name}
                </Pill>
              ))}
          </Text>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={t("academicYears.deleteConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={handleDeleteAcademicYear}
      />
      <AcademicYearModal
        open={academicYearModal}
        setOpen={setAcademicYearModal}
        academicYear={academicYear}
        handleSave={handleUpdateAcademicYear}
      />
    </Card>
  );
};

export default AcademicYearCard;
